
import { useState } from "react";
import { useAuth } from "./useAuth";
import { Student } from "@/types/models";
import { toast } from "sonner";
import { useDataServices } from "@/hooks/useDataServices";

export const useStudentActions = () => {
  const { organizationId } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { student: studentService } = useDataServices();

  const handleStudentAdded = async (newStudent: any) => {
    if (!organizationId) {
      toast.error("Organization ID not found");
      throw new Error("Organization ID not found");
    }

    setIsLoading(true);
    try {
      // Convert from form schema values (firstName, lastName) to database model (first_name, last_name)
      const studentData = {
        family_id: newStudent.family_id || newStudent.familyId,
        organization_id: organizationId,
        first_name: newStudent.firstName || newStudent.first_name,
        last_name: newStudent.lastName || newStudent.last_name,
        date_of_birth: newStudent.dateOfBirth || newStudent.date_of_birth,
        gender: newStudent.gender,
        notes: newStudent.notes
      };

      console.log("Adding student with data:", studentData);

      // Use the student service instead of direct Supabase call
      const data = await studentService.addStudent(studentData);
        
      toast.success("Student added successfully");
      
      return data;
    } catch (error) {
      console.error("Error adding student:", error);
      toast.error("Failed to add student");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleStudentUpdated = async (student: Student) => {
    if (!organizationId) {
      toast.error("Organization ID not found");
      throw new Error("Organization ID not found");
    }

    setIsLoading(true);
    try {
      // Use the student service
      const data = await studentService.updateStudent(student.id, student);
        
      toast.success("Student updated successfully");
      
      return data;
    } catch (error) {
      console.error("Error updating student:", error);
      toast.error("Failed to update student");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleStudentDeleted = async (studentId: string) => {
    if (!organizationId) {
      toast.error("Organization ID not found");
      throw new Error("Organization ID not found");
    }

    setIsLoading(true);
    try {
      // Use the student service
      const success = await studentService.deleteStudent(studentId);
        
      if (success) {
        toast.success("Student deleted successfully");
      }
      
      return success;
    } catch (error) {
      console.error("Error deleting student:", error);
      toast.error("Failed to delete student");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleStudentAdded,
    handleStudentUpdated,
    handleStudentDeleted
  };
};
